import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import BEMHelper from '@ynap/bem-helper'
import { LineItem } from '@ynap/line-item'
import { Link } from 'react-router-dom'
import { useConfig } from '@ynap/immutables-provider'
import { processBadges } from '../Returns/utils'
import RefundForm from 'app/components/RefundForm'

const bem = new BEMHelper('ReturnReview')

const propTypes = {
    messages: PropTypes.shape({
        orderDetails: PropTypes.shape({
            title: PropTypes.func.isRequired,
            orderInfo: PropTypes.func.isRequired,
            trackOrder: PropTypes.func.isRequired,
            dateShipped: PropTypes.func.isRequired,
            returnInfo: PropTypes.func,
            pageError: PropTypes.func.isRequired,
            retryButton: PropTypes.func.isRequired,
        }),
    }),
    externalOrderId: PropTypes.string,
    paymentMethod: PropTypes.array,
    refundPaymentMethods: PropTypes.array.isRequired,
    locale: PropTypes.object.isRequired,
    orderItems: PropTypes.arrayOf(
        PropTypes.shape({
            partNumber: PropTypes.string,
            image: PropTypes.string,
            brand: PropTypes.string,
            name: PropTypes.string,
            colour: PropTypes.string,
            size: PropTypes.string,
            quantity: PropTypes.string,
            price: PropTypes.object,
        })
    ),
    createReturn: PropTypes.func.isRequired,
    returnOrder: PropTypes.object.isRequired,
    returnItems: PropTypes.array.isRequired,
    returnOrderError: PropTypes.object,
    helpPage: PropTypes.string,
    urlPath: PropTypes.string,
    country: PropTypes.string,
    email: PropTypes.string,
    guestUser: PropTypes.bool,
    storeCreditExclusionList: PropTypes.array,
    storeCreditReturnsExclusionList: PropTypes.array,
}

function ReturnReview({
    messages,
    externalOrderId,
    refundPaymentMethods,
    paymentMethod,
    locale,
    createReturn,
    returnOrder,
    returnOrderError,
    returnItems,
    helpPage,
    urlPath,
    country,
    email,
    guestUser,
    storeCreditExclusionList,
    storeCreditReturnsExclusionList,
    config,
}) {
    const {
        head: { flags: { showStatusAsBadge = false } = {} },
    } = useConfig()

    return (
        <Fragment>
            <div className={bem('confirmInfo')}>
                <h2 className={bem('confirmTitle')}>{messages.orderDetails.confirmReturnTitle()}</h2>
                <p className={bem('confirmMessage')}>
                    {messages.orderDetails.confirmMessage({ numItems: returnItems?.length })}
                </p>
            </div>

            {returnItems?.map((item, index) => {
                const { isItemReturnable, primeLineNo } = item
                const itemReturnLine = returnOrder?.returnLines.find(({ lineNumber }) => lineNumber === primeLineNo)

                const returnStatus = messages.orderDetails.returnStatus({
                    type: itemReturnLine?.returnOrExchange || '',
                })

                const isInReturnItems =
                    isItemReturnable &&
                    !!returnItems?.find(({ primeLineNo: returnItemPLN }) => returnItemPLN === primeLineNo)

                const processedBadges = processBadges(item, showStatusAsBadge, isInReturnItems, returnStatus)

                return (
                    <LineItem
                        key={`${item.partNumber}-${index}`}
                        messages={messages}
                        {...item}
                        badges={processedBadges}
                    >
                        {!showStatusAsBadge && <div className={bem('returnStatus')}>{returnStatus}</div>}
                    </LineItem>
                )
            })}

            <RefundForm
                guestUser={guestUser}
                paymentMethod={paymentMethod}
                refundPaymentMethods={refundPaymentMethods}
                returnOrder={returnOrder}
                messages={messages}
                createReturn={createReturn}
                includesReturn={returnOrder.returnLines.find((line) => line.returnOrExchange === 'return')}
                error={returnOrderError}
                initialValues={
                    refundPaymentMethods.length == 1 ? {
                        refundPaymentMethod: refundPaymentMethods[0],
                    } : {}
                }
                helpPage={helpPage}
                country={country}
                locale={locale}
                returnItems={returnItems}
                email={email}
                storeCreditExclusionList={storeCreditExclusionList}
                storeCreditReturnsExclusionList={storeCreditReturnsExclusionList}
                config={config}
            >
                <Link
                    to={`/${locale.id}/account/${urlPath}/${externalOrderId}/return`}
                    className={bem('reviewChanges')}
                >
                    {messages.orderDetails.reviewChanges()}
                </Link>
            </RefundForm>
        </Fragment>
    )
}

ReturnReview.propTypes = propTypes

export default ReturnReview
